import { Boundaries } from "@planzone/core";
import {
  Identifiable,
  ProjectResource,
  resourcesListingFilter,
} from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { reduceProjectResource } from "../../common/projectResource";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  project: null,
  projectResources: [],
  filter: resourcesListingFilter,
  totalPages: 0,
};

function handleRequest(
  state: State,
  { project, filter }: Boundaries["retrieveProjectResourcesListing"]["request"],
): State {
  return filter.page === 0 || state.project?.uuid !== project.uuid
    ? {
        project,
        projectResources: [],
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
        totalPages: 0,
      }
    : {
        ...state,
        request: setRequestStateToPending(),
        filter: { ...filter, page: filter.page + 1 },
      };
}

function handleSuccess(
  state: State,
  {
    project,
    projectResources,
    filter,
    totalPages,
  }: Boundaries["retrieveProjectResourcesListing"]["success"],
): State {
  // A late page of another project, or of criteria replaced since, would be
  // appended to the new list.
  return state.project?.uuid !== project.uuid ||
    filter.page !== state.filter.page - 1
    ? state
    : {
        ...state,
        projectResources:
          filter.page === 0
            ? projectResources
            : [...state.projectResources, ...projectResources],
        request: setRequestStateToSuccess(),
        totalPages,
      };
}

function handleFailure(
  state: State,
  { project }: Boundaries["retrieveProjectResourcesListing"]["failure"],
): State {
  return state.project?.uuid !== project.uuid
    ? state
    : { ...state, request: setRequestStateToFailure() };
}

// Added members are listed last, like in retrieveProjectResources.
function addProjectResources(
  state: State,
  projectResources: Identifiable<ProjectResource>[],
): State {
  const added = projectResources.filter(
    (projectResource) => projectResource.project.uuid === state.project?.uuid,
  );

  return added.length > 0
    ? { ...state, projectResources: [...state.projectResources, ...added] }
    : state;
}

function handleProjectResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return addProjectResources(state, [projectResource]);
}

function handleAddPlanzoneResourceToProjectSuccess(
  state: State,
  {
    projectResource,
    project,
  }: Boundaries["addPlanzoneResourceToProject"]["success"],
): State {
  return addProjectResources(state, [{ ...projectResource, project }]);
}

function handleAddPlanzoneResourcesToProjectSuccess(
  state: State,
  {
    projectResources,
    project,
  }: Boundaries["addPlanzoneResourcesToProject"]["success"],
): State {
  return addProjectResources(
    state,
    projectResources.map((projectResource) => ({
      ...projectResource,
      project,
    })),
  );
}

function handleResourceFromProjectRemoval(
  state: State,
  {
    projectResource,
    project,
  }: Boundaries["removeResourceFromProject"]["success"],
): State {
  return state.project?.uuid !== project.uuid
    ? state
    : {
        ...state,
        projectResources: state.projectResources.filter(
          ({ uuid }) => uuid !== projectResource.uuid,
        ),
      };
}

function handleResourceRemoval(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return {
    ...state,
    projectResources: state.projectResources.filter(
      (projectResource) => resource.uuid !== projectResource.resource.uuid,
    ),
  };
}

// A member (de)activated in the project stays listed, like in retrieveProjectResources: the
// active/inactive filter is about the resource itself.
function handleProjectResourceActivationChange(
  state: State,
  projectResource: Boundaries["disableProjectResource"]["success"],
): State {
  return {
    ...state,
    projectResources: state.projectResources.map((resource) =>
      resource.uuid === projectResource.uuid
        ? { ...resource, deactivated: projectResource.deactivated }
        : resource,
    ),
  };
}

function propagateEvent(state = initialState, event: Event): State {
  return {
    ...state,
    projectResources: state.projectResources.map((projectResource) =>
      reduceProjectResource(projectResource, event),
    ),
  };
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveProjectResourcesListingRequested]: handleRequest,
      [events.retrieveProjectResourcesListingSucceeded]: handleSuccess,
      [events.retrieveProjectResourcesListingFailed]: handleFailure,
      [events.addProjectMaterialResourceSucceeded]:
        handleProjectResourceAddition,
      [events.addProjectVirtualResourceSucceeded]:
        handleProjectResourceAddition,
      [events.addPlanzoneResourceToProjectSucceeded]:
        handleAddPlanzoneResourceToProjectSuccess,
      [events.addPlanzoneResourcesToProjectSucceeded]:
        handleAddPlanzoneResourcesToProjectSuccess,
      [events.removeResourceFromProjectSucceeded]:
        handleResourceFromProjectRemoval,
      [events.removeResourceFromPlanzoneSucceeded]: handleResourceRemoval,
      [events.disableProjectResourceSucceeded]:
        handleProjectResourceActivationChange,
      [events.activateProjectResourceSucceeded]:
        handleProjectResourceActivationChange,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
